/**
 * Cliente do relay WebSocket (`GET /v1/ws`). Dono unico do socket.
 *
 * Responsabilidade: manter UM socket vivo, autenticar no primeiro quadro,
 * renovar o token antes de ele vencer, e traduzir cada quadro recebido em uma
 * chamada de setter do `store.ts`.
 *
 * NAO e responsabilidade deste arquivo: guardar estado (`store.ts`), decidir
 * tela (`main.ts`), nem MANDAR mutacao. O relay e de mao unica por decisao da
 * C8: comando vai por `POST /v1/devices/{id}/commands`. Os dois unicos quadros
 * que sobem daqui sao `auth` e `ping`, e nenhum dos dois muda nada no servidor.
 *
 * ## O vencimento do token e invisivel, de proposito
 *
 * Um access token vale 15 min e um painel fica aberto a tarde inteira: o
 * vencimento nao e um evento do usuario, e a manutencao que esta camada faz
 * sozinha. Nada dele chega a tela — nem "sem conexao" por um instante, nem o
 * texto que o relay manda antes de fechar. Pedir "reconecte" a quem esta
 * olhando a estufa e transferir para ele um trabalho que e daqui.
 *
 * Por isso a renovacao SE REAGENDA: o `hello` so anuncia a validade do
 * primeiro token, entao um agendamento unico renovava uma vez e deixava o
 * socket morrer no vencimento seguinte. Quem sabe a validade do token de agora
 * e o `api.ts` (`validadeDoToken()`), dono unico da credencial.
 *
 * ## Tres relogios, e por que cada um existe
 *
 * - **renovacao** (validade do token menos folga): o servidor fecha com 4401
 *   quando o access token vence (C8). Renovar antes mantem o MESMO socket, sem
 *   snapshot novo. Sem isto, todo painel aberto piscaria a cada 15 min.
 * - **ping** (20 s): a API de WebSocket do navegador nao expoe o pong de
 *   PROTOCOLO, entao este e o unico jeito de medir o caminho de volta.
 * - **cao de guarda** (45 s sem NENHUM quadro): um socket sobre uma rede que
 *   sumiu (celular saindo do Wi-Fi, notebook suspenso) fica aberto do lado do
 *   navegador por minutos sem nunca disparar `onclose`. Sem este relogio a
 *   tela mostraria "ao vivo" para um socket morto — exatamente a desonestidade
 *   que o gate desta fase existe para impedir.
 */

import { garantirToken, SemRede, SemSessao, validadeDoToken } from "./api";
import * as store from "./store";
import type { QuadroWs } from "./tipos";

const PING_MS = 20_000;
const CAO_DE_GUARDA_MS = 45_000;
/** Renova com esta folga antes do `token_expires_at` que o `hello` anunciou. */
const FOLGA_RENOVACAO_MS = 90_000;
const BACKOFF_INICIAL_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/** Faixa privada, definida em `app/routers/ws.py`: renove e reconecte. */
const FECHOU_EXPIRADO = 4401;
/** Faixa privada: o tenant esta no teto de conexoes. Reconectar nao resolve. */
const FECHOU_LOTADO = 4429;
/** RFC 6455: credencial ausente/invalida, ou troca de tenant. */
const FECHOU_AUTH = 1008;

let socket: WebSocket | null = null;
let ligado = false;
let backoffMs = BACKOFF_INICIAL_MS;
let timerReconexao: number | null = null;
let timerPing: number | null = null;
let timerRenovacao: number | null = null;
let timerCao: number | null = null;
let pingEnviadoEm = 0;
/** Validade do token que este socket esta usando, em ms de epoca. */
let expiraSocketEm = 0;
/** Ja gastou a tentativa de renovar depois de um 1008? Zera a cada `hello`. */
let jaTentouRenovarNoAuth = false;
let aoPerderSessao: () => void = () => {};

export function iniciar(quandoPerderSessao: () => void): void {
  aoPerderSessao = quandoPerderSessao;
  if (ligado) return;
  ligado = true;
  window.addEventListener("online", agora);
  window.addEventListener("offline", aoFicarOffline);
  document.addEventListener("visibilitychange", aoVoltarParaAba);
  void conectar();
}

export function parar(): void {
  ligado = false;
  window.removeEventListener("online", agora);
  window.removeEventListener("offline", aoFicarOffline);
  document.removeEventListener("visibilitychange", aoVoltarParaAba);
  limparRelogios();
  if (timerReconexao !== null) clearTimeout(timerReconexao);
  timerReconexao = null;
  const anterior = socket;
  socket = null;
  anterior?.close(1000, "saindo");
}

/** Tenta agora, zerando o backoff. Usado quando o SO diz que a rede voltou. */
function agora(): void {
  backoffMs = BACKOFF_INICIAL_MS;
  if (timerReconexao !== null) {
    clearTimeout(timerReconexao);
    timerReconexao = null;
  }
  if (socket === null) void conectar();
}

function aoFicarOffline(): void {
  store.definirConexao("sem-rede");
}

function aoVoltarParaAba(): void {
  // Aba em segundo plano tem timer estrangulado pelo navegador: o cao de
  // guarda pode nao ter corrido enquanto a tela estava escondida, e o socket
  // pode estar morto ha minutos. Voltar para a aba e o momento de conferir.
  if (document.visibilityState !== "visible" || !ligado) return;
  if (socket === null) {
    agora();
    return;
  }
  // O timer de renovacao roda no mesmo relogio estrangulado, entao um celular
  // que passou a noite com a aba aberta volta com o token vencendo — ou ja
  // vencido — e o socket ainda aberto. Renovar aqui e o que impede o 4401 de
  // acontecer, em vez de so limpar a bagunca depois.
  // `0` = o `hello` ainda nao chegou: nao ha validade conhecida para conferir.
  if (expiraSocketEm !== 0 && Date.now() > expiraSocketEm - FOLGA_RENOVACAO_MS) {
    renovar(socket);
  }
}

async function conectar(): Promise<void> {
  if (!ligado || socket !== null) return;
  store.definirConexao("conectando");

  let token: string;
  try {
    // Token ANTES de abrir: o servidor derruba o socket que nao autenticar em
    // 5 s (`AUTH_TIMEOUT_S`), e abrir para so entao descobrir que a sessao
    // acabou gastaria uma conexao para nada.
    token = await garantirToken();
  } catch (erro) {
    if (erro instanceof SemSessao) {
      aoPerderSessao();
      return;
    }
    if (erro instanceof SemRede) store.definirConexao("sem-rede");
    reagendar();
    return;
  }
  if (!ligado) return;

  const esquema = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${esquema}://${location.host}/v1/ws`);
  socket = ws;
  expiraSocketEm = 0; // so o `hello` deste socket diz de novo qual e a validade

  ws.onopen = () => {
    ws.send(JSON.stringify({ type: "auth", token }));
    backoffMs = BACKOFF_INICIAL_MS;
    store.definirConexao("ao-vivo");
    timerPing = window.setInterval(() => enviarPing(ws), PING_MS);
    reiniciarCao();
  };

  ws.onmessage = (evento) => {
    reiniciarCao();
    tratar(evento.data as string);
  };

  ws.onerror = () => {
    // `onclose` vem logo depois e e la que a reconexao e decidida; aqui so
    // evita o "Uncaught" no console do navegador.
  };

  ws.onclose = (evento) => {
    if (socket !== ws) return; // socket ja substituido: fechamento antigo
    socket = null;
    limparRelogios();
    if (!ligado) return;

    if (evento.code === FECHOU_AUTH) {
      // O servidor recusou a credencial. Isso quase sempre e sessao acabada,
      // mas nao sempre: um token que o servidor deixou de aceitar (segredo
      // trocado num deploy, relogio fora de hora) chega aqui igualzinho, e
      // enquanto o cookie de refresh viver ha o que fazer. Uma tentativa por
      // conexao bem-sucedida — sem o limite, um token que o servidor recusa
      // por outro motivo viraria laco de renovacao.
      if (jaTentouRenovarNoAuth) {
        aoPerderSessao();
        return;
      }
      jaTentouRenovarNoAuth = true;
      store.definirConexao("conectando");
      void garantirToken(true)
        .then(agora)
        .catch((erro: unknown) => {
          if (erro instanceof SemRede) {
            // A rede caiu no meio da renovacao: isto nao prova nada sobre a
            // sessao, entao a tentativa volta a valer quando a rede voltar.
            jaTentouRenovarNoAuth = false;
            store.definirConexao("sem-rede");
            reagendar();
            return;
          }
          aoPerderSessao();
        });
      return;
    }

    if (evento.code === FECHOU_EXPIRADO) {
      // Manutencao, nao queda: o servidor so disse que ESTE access token
      // venceu, e o cookie de refresh continua valido. Fica em "conectando" —
      // marcar "sem-rede" acenderia o alarme vermelho da barra de estado por
      // uma troca de credencial que o usuario nao pediu e nao pode ajudar.
      store.definirConexao("conectando");
      void garantirToken(true)
        .then(agora)
        .catch((erro: unknown) => {
          // O refresh e que morreu: ai sim a sessao acabou. Sem isto, uma
          // sessao vencida virava reconexao eterna contra um 401 fixo.
          if (erro instanceof SemSessao) aoPerderSessao();
          else {
            store.definirConexao("sem-rede");
            reagendar();
          }
        });
      return;
    }

    store.definirConexao("sem-rede");
    if (evento.code === FECHOU_LOTADO) {
      // Reconectar so vai bater no mesmo teto: aqui o usuario tem o que fazer,
      // e e a unica razao de fechamento que vale uma frase na tela.
      store.avisar(
        "Muitas telas abertas nesta conta. Feche uma para este painel voltar ao vivo.",
      );
    }
    reagendar();
  };
}

function reagendar(): void {
  if (!ligado || timerReconexao !== null) return;
  // Jitter para nao sincronizar todas as abas do mundo no mesmo instante
  // depois de uma queda do servidor.
  const atraso = backoffMs + Math.random() * backoffMs * 0.3;
  timerReconexao = window.setTimeout(() => {
    timerReconexao = null;
    void conectar();
  }, atraso);
  backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
}

function limparRelogios(): void {
  if (timerPing !== null) clearInterval(timerPing);
  if (timerRenovacao !== null) clearTimeout(timerRenovacao);
  if (timerCao !== null) clearTimeout(timerCao);
  timerPing = null;
  timerRenovacao = null;
  timerCao = null;
}

function reiniciarCao(): void {
  if (timerCao !== null) clearTimeout(timerCao);
  timerCao = window.setTimeout(() => {
    // Nenhum quadro em 45 s, nem o pong do nosso proprio ping: o socket esta
    // morto mesmo que o navegador ainda o considere aberto.
    socket?.close(4000, "sem resposta");
  }, CAO_DE_GUARDA_MS);
}

function enviarPing(ws: WebSocket): void {
  if (ws.readyState !== WebSocket.OPEN) return;
  pingEnviadoEm = performance.now();
  ws.send(JSON.stringify({ type: "ping" }));
}

function agendarRenovacao(expiraEmMs: number, ws: WebSocket): void {
  if (timerRenovacao !== null) clearTimeout(timerRenovacao);
  expiraSocketEm = expiraEmMs;
  const atraso = expiraEmMs - Date.now() - FOLGA_RENOVACAO_MS;
  timerRenovacao = window.setTimeout(() => renovar(ws), Math.max(atraso, 1_000));
}

/**
 * Renova o token e reautentica o socket, sem derruba-lo.
 *
 * Reagenda a si mesma: o `hello` so anunciou a validade do primeiro token, e
 * uma renovacao que nao marca a proxima deixa o socket morrer no vencimento
 * seguinte — que era exatamente de onde vinha o 4401 que o usuario via.
 */
function renovar(ws: WebSocket): void {
  void garantirToken(true)
    .then((token) => {
      if (ws.readyState !== WebSocket.OPEN || socket !== ws) return;
      // Mesmo quadro do primeiro `auth`: renova sem derrubar o socket e
      // sem refazer o snapshot (C8).
      ws.send(JSON.stringify({ type: "auth", token }));
      agendarRenovacao(validadeDoToken(), ws);
    })
    .catch((erro: unknown) => {
      if (erro instanceof SemSessao) aoPerderSessao();
      // Sem rede: o socket vai cair sozinho e o 4401 cuida do resto.
    });
}

function tratar(bruto: string): void {
  let quadro: QuadroWs;
  try {
    quadro = JSON.parse(bruto) as QuadroWs;
  } catch {
    return;
  }

  switch (quadro.type) {
    case "hello":
      // Chegou ate aqui: o servidor aceitou a credencial, entao a tentativa de
      // recuperacao do 1008 volta a estar disponivel para a proxima vez.
      jaTentouRenovarNoAuth = false;
      if (socket !== null) agendarRenovacao(Date.parse(quadro.token_expires_at), socket);
      break;
    case "snapshot":
      store.aplicarSnapshot(quadro.devices);
      break;
    case "telemetry":
      store.aplicarTelemetria(quadro.device_id, quadro.samples, quadro.backfill);
      break;
    case "state":
      store.aplicarEstado(quadro.device_id, quadro.state, quadro.updated_at);
      break;
    case "online":
      store.aplicarPresenca(quadro.device_id, quadro.online, quadro.fw);
      break;
    case "command":
      store.aplicarComando(quadro.device_id, quadro.command);
      break;
    case "upload":
      store.aplicarUpload(quadro.device_id, quadro.upload);
      break;
    case "alert":
      // Chega tambem pela bandeja do sistema, quando ha push — e os dois
      // caminhos convivem de proposito. O push acorda quem nao esta olhando; o
      // quadro atualiza a tela de quem esta, sem esperar um GET. O `id` e o
      // mesmo nos dois, entao um alerta que chegue pelos dois nao vira dois.
      store.aplicarAlerta(quadro.alert);
      break;
    case "pong":
      store.definirLatencia(Math.round(performance.now() - pingEnviadoEm));
      break;
    case "error":
      // O relay manda isto logo antes de fechar, com o texto do SERVIDOR.
      // Nao vai para a tela: quem decide o que o usuario precisa saber e o
      // `onclose`, que tem o codigo do fechamento e sabe se a PWA resolve
      // sozinha. Pintar o texto cru aqui punha "token expirado, reconecte"
      // na frente de quem nao tem nada a fazer a respeito — e o aviso ainda
      // ficava na tela depois de a reconexao ja ter dado certo.
      console.debug("relay:", quadro.err);
      break;
  }
}
